import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Layout from "../components/layout/Layout";
import { API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Skeleton } from "../components/ui/skeleton";
import { toast } from "sonner";
import { AlertTriangle, Building2, CreditCard, Loader2, Receipt } from "lucide-react";
import {
  extractApiErrorMessage,
  formatMoney,
  humanizeStatus,
  normalizeAdminOrder,
} from "../lib/safeData";

const MANUAL_METHODS = [
  { id: "bank_transfer", label: "Bank Transfer", icon: Building2, referenceLabel: "Transfer reference" },
  { id: "firstpay", label: "FirstPay (CIBC FirstCaribbean)", icon: CreditCard, referenceLabel: "Transaction ID" },
];

/**
 * Payment screen for an order an admin created by hand.
 *
 * Reached through the link generated in /admin/orders. The cart-driven
 * CheckoutPage cannot be used here because the order already exists and the
 * customer's cart is empty.
 */
export default function ManualOrderPaymentPage({ orderId }) {
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [claimError, setClaimError] = useState("");
  const [methods, setMethods] = useState([]);
  const [bankDetails, setBankDetails] = useState(null);
  const [selectedMethod, setSelectedMethod] = useState("bank_transfer");
  const [payerName, setPayerName] = useState("");
  const [reference, setReference] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const claimOrder = useCallback(async () => {
    setLoading(true);
    setClaimError("");
    try {
      const response = await axios.post(
        `${API}/orders/${orderId}/claim`,
        {},
        { withCredentials: true }
      );
      setOrder(normalizeAdminOrder(response.data));
    } catch (error) {
      console.error("Could not open manual order:", error);
      setClaimError(extractApiErrorMessage(error, "This payment link is no longer valid"));
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    claimOrder();
  }, [claimOrder]);

  useEffect(() => {
    if (!order) return;
    axios
      .get(`${API}/payments/methods`, {
        params: { destination: order.destination || "BB" },
        withCredentials: true,
      })
      .then((response) => {
        const available = Array.isArray(response.data?.methods) ? response.data.methods : [];
        setMethods(available);
        const bank = available.find((method) => method.id === "bank_transfer");
        setBankDetails(bank?.bank_details || null);
        const firstManual = MANUAL_METHODS.find((method) =>
          available.some((entry) => entry.id === method.id)
        );
        if (firstManual) setSelectedMethod(firstManual.id);
      })
      .catch((error) => {
        console.error("Could not load payment methods:", error);
        setMethods([]);
      });
  }, [order]);

  const availableManualMethods = MANUAL_METHODS.filter((method) =>
    methods.some((entry) => entry.id === method.id)
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!payerName.trim() || !reference.trim()) {
      toast.error("Enter the payer name and the payment reference");
      return;
    }

    setSubmitting(true);
    try {
      const endpoint =
        selectedMethod === "firstpay"
          ? `${API}/payments/firstpay/initiate`
          : `${API}/payments/bank-transfer/initiate`;
      const payload =
        selectedMethod === "firstpay"
          ? { order_id: order.order_id, payer_name: payerName, transaction_id: reference }
          : { order_id: order.order_id, payer_name: payerName, transfer_reference: reference };

      await axios.post(endpoint, payload, { withCredentials: true });
      toast.success("Payment submitted for verification");
      navigate(`/order-confirmation?order_id=${order.order_id}`);
    } catch (error) {
      console.error("Manual order payment failed:", error);
      toast.error(extractApiErrorMessage(error, "Failed to submit payment"));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="mx-auto max-w-2xl px-4 py-12">
          <Skeleton className="mb-4 h-8 w-1/2" />
          <Skeleton className="h-48 w-full" />
        </div>
      </Layout>
    );
  }

  if (claimError) {
    return (
      <Layout>
        <div className="mx-auto max-w-2xl px-4 py-12">
          <div
            className="rounded-lg border border-amber-200 bg-amber-50 p-6"
            data-testid="manual-order-claim-error"
          >
            <div className="mb-2 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              <h1 className="text-lg font-semibold text-amber-900">
                This payment link can&apos;t be opened
              </h1>
            </div>
            <p className="mb-4 text-sm text-amber-800">{claimError}</p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={claimOrder}>
                Try again
              </Button>
              <Button variant="outline" onClick={() => navigate("/products")}>
                Browse products
              </Button>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="mx-auto max-w-2xl px-4 py-12" data-testid="manual-order-payment">
        <div className="mb-6 flex items-center gap-2">
          <Receipt className="h-6 w-6 text-orange-600" />
          <h1 className="text-2xl font-bold text-slate-900">Complete your payment</h1>
        </div>

        <div className="mb-6 rounded-lg border border-slate-200 bg-white p-6">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <div>
              <p className="text-sm text-slate-500">Order</p>
              <p className="mono font-medium text-slate-900">{order.order_id}</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-slate-500">Amount due</p>
              <p className="text-2xl font-bold text-slate-900" data-testid="manual-order-amount">
                {formatMoney(order.total, order.currency)}
              </p>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-4 border-t border-slate-100 pt-4 text-sm">
            <div>
              <p className="text-slate-500">Status</p>
              <p className="font-medium text-slate-900">{humanizeStatus(order.status)}</p>
            </div>
            <div>
              <p className="text-slate-500">Destination</p>
              <p className="font-medium text-slate-900">{order.destination}</p>
            </div>
          </div>
          {order.notes ? (
            <p className="mt-4 border-t border-slate-100 pt-4 text-sm text-slate-600">
              {order.notes}
            </p>
          ) : null}
        </div>

        {availableManualMethods.length === 0 ? (
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-6 text-sm text-slate-600">
            No self-service payment method is available for {order.destination}. Reply to the
            message that contained this link and our concierge team will arrange payment.
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            className="rounded-lg border border-slate-200 bg-white p-6"
            data-testid="manual-order-payment-form"
          >
            <div className="mb-4 flex flex-wrap gap-2">
              {availableManualMethods.map((method) => (
                <button
                  key={method.id}
                  type="button"
                  onClick={() => setSelectedMethod(method.id)}
                  className={`flex items-center gap-2 rounded-md border px-4 py-2 text-sm ${
                    selectedMethod === method.id
                      ? "border-orange-500 bg-orange-50 text-orange-800"
                      : "border-slate-200 text-slate-600 hover:bg-slate-50"
                  }`}
                  data-testid={`manual-order-method-${method.id}`}
                >
                  <method.icon className="h-4 w-4" />
                  {method.label}
                </button>
              ))}
            </div>

            {selectedMethod === "bank_transfer" && bankDetails ? (
              <dl className="mb-4 rounded-md bg-slate-50 p-4 text-sm">
                {Object.entries(bankDetails).map(([key, value]) => (
                  <div key={key} className="flex justify-between gap-4 py-0.5">
                    <dt className="capitalize text-slate-500">{key.replace(/_/g, " ")}</dt>
                    <dd className="text-right font-medium text-slate-900">{String(value)}</dd>
                  </div>
                ))}
              </dl>
            ) : null}

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="payer-name">Payer name *</Label>
                <Input
                  id="payer-name"
                  value={payerName}
                  onChange={(e) => setPayerName(e.target.value)}
                  placeholder="Name on the transfer"
                  data-testid="manual-order-payer-name"
                />
              </div>
              <div>
                <Label htmlFor="payment-reference">
                  {availableManualMethods.find((m) => m.id === selectedMethod)?.referenceLabel ||
                    "Reference"}{" "}
                  *
                </Label>
                <Input
                  id="payment-reference"
                  value={reference}
                  onChange={(e) => setReference(e.target.value)}
                  placeholder={order.order_id}
                  data-testid="manual-order-reference"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={submitting}
              className="mt-4 w-full bg-orange-600 hover:bg-orange-700"
              data-testid="manual-order-pay-submit"
            >
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                `Submit payment of ${formatMoney(order.total, order.currency)}`
              )}
            </Button>
            <p className="mt-3 text-xs text-slate-500">
              Our team verifies the transfer before the order moves to fulfilment.
            </p>
          </form>
        )}
      </div>
    </Layout>
  );
}
